import {
  analyzeRootPuppyAssemblyDirectChildDiff,
  isPuppyAssemblyPackageRoot,
  type AssemblyDocument,
  type ModelAssetNode,
  type ProjectModelLoadResult,
  type PuppyAssemblyProjectModelCoverageContext,
  type RootPuppyAssemblyDirectChildDiff,
} from '@puppycad/cad-core'

export { isPuppyAssemblyPackageRoot } from '@puppycad/cad-core'

/** `.puppy + _assembly.json` 装配 direct child 检查输入。 */
export interface CheckPuppyAssemblyDirectChildrenInput {
  /** 当前加载单元结果，其 root 就是本次检查边界。 */
  readonly currentResult: ProjectModelLoadResult
  /** 调用方已确认的精确 project-model coverage 上下文。 */
  readonly coverageContext: PuppyAssemblyProjectModelCoverageContext | null
  /** complete GLB occurrence 图提供的当前选中根一级 instance_id coverage。 */
  readonly completeGlbCoverage?: PuppyCADWeb.CompleteGlbDirectChildCoverage
}

/** `.puppy + _assembly.json` 装配 direct child 检查结果。 */
export interface PuppyAssemblyDirectChildCheckResult {
  /** 本次检查的装配根。 */
  readonly root: ModelAssetNode
  /** 当前节点自己的装配文档。 */
  readonly assemblyDocument: AssemblyDocument | null
  /** 当前实际 `.puppy` 文件路径；无可用 project-model 上下文时为 null。 */
  readonly packageFileId: string | null
  /** 已物化 project-model 的根直系 assemblyNodeId。 */
  readonly rootDirectAssemblyNodeIds: ReadonlySet<string>
  /** 精确 coverage 差异；上下文不可用时为 null。 */
  readonly diff: RootPuppyAssemblyDirectChildDiff | null
}

/**
 * 创建无 bridge coverage 上下文的稳定结果。
 *
 * @param root 当前检查根。
 * @param assemblyDocument 已解析的装配文档。
 * @returns 不触发后端补树的兼容结果。
 */
function createEmptyResult(
  root: ModelAssetNode,
  assemblyDocument: AssemblyDocument | null = null,
): PuppyAssemblyDirectChildCheckResult {
  return {
    root,
    assemblyDocument,
    packageFileId: null,
    rootDirectAssemblyNodeIds: new Set(),
    diff: null,
  }
}

/**
 * 统一检查当前 assembly `.puppy` 节点自己的外部 direct child coverage。
 *
 * @param input 当前加载单元与可选的显式 project-model 上下文。
 * @returns 精确 node_where 差异；legacy 上下文不可用时返回空结果。
 */
export async function checkPuppyAssemblyDirectChildren(
  input: CheckPuppyAssemblyDirectChildrenInput,
): Promise<PuppyAssemblyDirectChildCheckResult> {
  const root = input.currentResult.root
  if (!input.completeGlbCoverage && !isPuppyAssemblyPackageRoot(root)) {
    return createEmptyResult(root)
  }

  const assemblyDocument = input.currentResult.assemblyDocumentsByNodeKey.get(root.nodeKey) ?? null
  if (!assemblyDocument) {
    return createEmptyResult(root)
  }

  const coverageContext = input.coverageContext
  if (!coverageContext && !input.completeGlbCoverage) {
    // v4 无法生成 project-model 时保留既有迁移边界，不把未知覆盖状态误报为新增 RPC。
    return createEmptyResult(root, assemblyDocument)
  }

  const diff = analyzeRootPuppyAssemblyDirectChildDiff({
    root,
    document: assemblyDocument,
    ...(coverageContext
      ? {
          standardPuppyCoverage: {
            packageFileId: coverageContext.packageFileId,
            rootDirectAssemblyNodeIds: coverageContext.rootDirectAssemblyNodeIds,
          },
        }
      : input.completeGlbCoverage
        ? {
            standardPuppyCoverage: {
              packageFileId: input.completeGlbCoverage.packageFileId,
              rootDirectAssemblyNodeIds: input.completeGlbCoverage.rootDirectAssemblyNodeIds,
            },
          }
        : {}),
    ...(input.completeGlbCoverage
      ? {
          completeGlbCoverage: {
            rootDirectInstanceIds: input.completeGlbCoverage.rootDirectInstanceIds,
          },
        }
      : {}),
  })

  return {
    root,
    assemblyDocument,
    packageFileId: coverageContext?.packageFileId
      ?? input.completeGlbCoverage?.packageFileId
      ?? null,
    rootDirectAssemblyNodeIds: coverageContext?.rootDirectAssemblyNodeIds
      ?? input.completeGlbCoverage?.rootDirectAssemblyNodeIds
      ?? new Set(),
    diff,
  }
}
